// Painel Super Master > ambiente dedicado a um dispositivo JT/T 808
// (câmera WY02 4G AI ADAS DSM, IMEI 860879071102483 hoje) — separado do
// mapa principal de propósito, ver plano da integração da câmera.
// Parte A (fotos de alarme 0x0800/0x0801) e Parte B (vídeo ao vivo JT/T
// 1078, via mpegts.js) implementadas aqui — 1078 ainda NÃO validado
// contra a câmera real, ver README do módulo jt808 no backend.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ImageOff, Loader2, Mic, RefreshCw, Square, TriangleAlert } from "lucide-react";
import type Mpegts from "mpegts.js";
import { MasterShell } from "@/components/master-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useJt808CameraPhotos } from "@/hooks/queries/platform";
import { jt808CameraService, type Jt808CameraPhoto } from "@/services/jt808-camera.service";
import { encodePcmToMuLaw8k } from "@/lib/mulaw-encoder";

export const Route = createFileRoute("/master/camera/$imei")({
  component: () => (
    <MasterShell>
      <Jt808CameraPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Câmera JT808 — Super Master — GPS Drive" }] }),
});

function Jt808CameraPage() {
  const { imei } = Route.useParams();
  const { data: photos, isLoading, isError, refetch, isFetching } = useJt808CameraPhotos(imei);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Câmera JT808</h1>
          <p className="text-sm text-muted-foreground">IMEI {imei} — ambiente de diagnóstico, fora do mapa principal.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <LiveVideoCard imei={imei} />

      <Card>
        <CardHeader>
          <CardTitle>Fotos de alarme</CardTitle>
          <CardDescription>Capturadas pela câmera em eventos de alarme (DSM/ADAS) e enviadas via JT/T 808.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : isError ? (
            <p className="text-sm text-destructive">Falha ao carregar — servidor da câmera de teste pode estar fora do ar.</p>
          ) : !photos || photos.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
              <ImageOff className="h-8 w-8" strokeWidth={1.5} />
              <p className="text-sm">Nenhuma foto de alarme recebida ainda.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {photos.map((photo) => (
                <PhotoThumb key={photo.file} photo={photo} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type LiveState = "idle" | "connecting" | "playing" | "error";

// mpegts.js: carregado sob demanda (só quando alguém aperta "Iniciar") —
// evita puxar a lib no bundle principal do app pra uma tela de
// diagnóstico de um único dispositivo de teste.
function LiveVideoCard({ imei }: { imei: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Mpegts.Player | null>(null);
  const [state, setState] = useState<LiveState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Confirmado em campo 2026-09-15: WY02 tem 2 câmeras físicas — canal 1
  // (externa, voltada pra fora) e canal 2 (interna, cabine/DSM).
  const [channel, setChannel] = useState<1 | 2>(1);

  const stop = () => {
    playerRef.current?.pause();
    playerRef.current?.unload();
    playerRef.current?.detachMediaElement();
    playerRef.current?.destroy();
    playerRef.current = null;
    setState("idle");
  };

  useEffect(() => stop, []);

  const start = async (targetChannel: 1 | 2) => {
    stop();
    setChannel(targetChannel);
    setErrorMessage(null);
    setState("connecting");
    try {
      const mpegts = (await import("mpegts.js")).default;
      if (!mpegts.isSupported() || !videoRef.current) {
        setState("error");
        setErrorMessage("Este navegador não suporta reprodução de vídeo ao vivo (Media Source Extensions).");
        return;
      }
      const { url, headers } = jt808CameraService.getLiveVideoRequest(imei, targetChannel);
      const player = mpegts.createPlayer({ type: "flv", isLive: true, url }, { headers });
      playerRef.current = player;
      player.on(mpegts.Events.ERROR, (_type: unknown, _detail: unknown, info: unknown) => {
        setState("error");
        setErrorMessage(
          `Falha na transmissão — câmera pode não ter aceitado o pedido de vídeo (JT/T 1078 ainda não confirmado contra este hardware). Detalhe: ${JSON.stringify(info)}`,
        );
      });
      player.on(mpegts.Events.LOADING_COMPLETE, () => setState("idle"));
      player.attachMediaElement(videoRef.current);
      player.load();
      await player.play();
      setState("playing");
    } catch (err) {
      setState("error");
      setErrorMessage(err instanceof Error ? err.message : "Falha desconhecida ao iniciar o player.");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Vídeo ao vivo</CardTitle>
          <CardDescription>JT/T 1078 — confirmado funcionando contra a WY02 real. Pode levar ~15s pra conectar.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-border">
            <Button
              variant={channel === 1 && state !== "idle" ? "default" : "ghost"}
              size="sm"
              className="rounded-none"
              onClick={() => start(1)}
            >
              Externa
            </Button>
            <Button
              variant={channel === 2 && state !== "idle" ? "default" : "ghost"}
              size="sm"
              className="rounded-none"
              onClick={() => start(2)}
            >
              Interna
            </Button>
          </div>
          {(state === "playing" || state === "connecting") && (
            <Button variant="outline" size="sm" onClick={stop}>
              <Square className="mr-2 h-4 w-4" />
              Parar
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} className="h-full w-full" muted playsInline controls />
          {state === "connecting" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="text-sm">Aguardando a câmera conectar…</p>
            </div>
          )}
          {state === "idle" && (
            <p className="absolute text-sm text-muted-foreground">Escolha "Externa" ou "Interna" para começar.</p>
          )}
        </div>
        {state === "error" && errorMessage && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}
        <PushToTalkButton imei={imei} disabled={state !== "playing"} />
      </CardContent>
    </Card>
  );
}

type TalkState = "idle" | "recording" | "sending" | "sent" | "error";

// EXPERIMENTAL — locução (push-to-talk): segura o botão, fala, solta pra
// enviar. Direção plataforma→terminal nunca documentada nem testada
// antes de 2026-09-15 (ver README do módulo jt808 no backend) — testado
// e autorizado em campo pelo usuário. Só funciona com vídeo ao vivo já
// em andamento (mesma conexão 1078, ver jt808-server.ts).
function PushToTalkButton({ imei, disabled }: { imei: string; disabled: boolean }) {
  const [state, setState] = useState<TalkState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Float32Array[]>([]);

  const cleanupAudio = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close();
    audioCtxRef.current = null;
  };

  useEffect(() => cleanupAudio, []);

  const startRecording = async () => {
    if (disabled || state === "recording") return;
    setMessage(null);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      // ScriptProcessorNode está deprecated mas ainda amplamente
      // suportado — suficiente pra um teste de locução; trocar por
      // AudioWorklet se isso virar feature definitiva.
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processor.onaudioprocess = (e) => {
        chunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(audioCtx.destination);
      setState("recording");
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : "Não foi possível acessar o microfone.");
    }
  };

  const stopAndSend = async () => {
    if (state !== "recording") return;
    const sampleRate = audioCtxRef.current?.sampleRate ?? 48000;
    cleanupAudio();
    const totalLength = chunksRef.current.reduce((sum, c) => sum + c.length, 0);
    if (totalLength === 0) {
      setState("idle");
      return;
    }
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of chunksRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    chunksRef.current = [];
    const encoded = encodePcmToMuLaw8k(merged, sampleRate);
    setState("sending");
    try {
      const result = await jt808CameraService.sendTalkAudio(imei, encoded);
      setState(result.ok ? "sent" : "error");
      setMessage(result.message);
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : "Falha ao enviar áudio.");
    }
  };

  return (
    <div className="mt-3 flex items-center gap-3">
      <Button
        variant={state === "recording" ? "destructive" : "outline"}
        size="sm"
        disabled={disabled || state === "sending"}
        onMouseDown={startRecording}
        onMouseUp={stopAndSend}
        onMouseLeave={() => state === "recording" && stopAndSend()}
        onTouchStart={startRecording}
        onTouchEnd={stopAndSend}
      >
        <Mic className="mr-2 h-4 w-4" />
        {state === "recording" ? "Gravando… solte para enviar" : "Segure para falar com o motorista"}
      </Button>
      {state === "sending" && <span className="text-sm text-muted-foreground">Enviando…</span>}
      {message && (
        <span className={`text-sm ${state === "error" ? "text-destructive" : "text-muted-foreground"}`}>{message}</span>
      )}
    </div>
  );
}

function PhotoThumb({ photo }: { photo: Jt808CameraPhoto }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    jt808CameraService
      .fetchPhotoBlob(photo)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo]);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background/60">
      <div className="flex aspect-square items-center justify-center bg-muted">
        {failed ? (
          <ImageOff className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
        ) : objectUrl ? (
          <img src={objectUrl} alt={photo.file} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full animate-pulse bg-muted-foreground/10" />
        )}
      </div>
      <div className="p-2 text-xs text-muted-foreground">
        {new Date(photo.modifiedAt).toLocaleString("pt-BR")}
      </div>
    </div>
  );
}
